import { MAX_SELF_SERVE_PAGES } from '@repo/contracts/services-pricing';

export const MAX_FILES = 5;
export const MAX_FILE_BYTES = 20 * 1024 * 1024;
export const ALLOWED_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xlsx',
  'png',
  'jpg',
  'jpeg',
  'zip',
  'txt',
  'md',
];

export interface RequestForm {
  name: string;
  email: string;
  company: string;
  role: string;
  phone: string;
  url: string;
  additionalUrls: string;
  estimatedPages: string;
  standard: string;
  requiresLogin: boolean;
  deadline: string;
  notes: string;
  website: string;
}

export type FormErrors = Partial<Record<keyof RequestForm | 'files', string>>;

/** "example.com" → "https://example.com"; null when it can't be a web URL. */
export function normalizeUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if (!url.hostname.includes('.')) return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function splitUrls(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function fileProblem(file: File): string | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return `${file.name} is not a supported file type.`;
  }
  if (file.size > MAX_FILE_BYTES) {
    return `${file.name} is larger than 20 MB.`;
  }
  return null;
}

/** Field order here is the order errors are listed and focused in. */
export function validate(form: RequestForm, files: File[]): FormErrors {
  const errors: FormErrors = {};
  if (!form.name.trim()) errors.name = 'Enter your name.';
  if (!EMAIL.test(form.email.trim())) {
    errors.email = 'Enter a work email address, like name@company.com.';
  }
  if (!form.company.trim())
    errors.company = 'Enter your company or organisation.';
  if (!normalizeUrl(form.url)) {
    errors.url =
      'Enter the address of the site to audit, like https://example.com.';
  }
  const extra = splitUrls(form.additionalUrls);
  if (extra.length > 50) {
    errors.additionalUrls = 'List up to 50 additional addresses.';
  } else {
    const bad = extra.find((u) => !normalizeUrl(u));
    if (bad) errors.additionalUrls = `"${bad}" isn't a web address.`;
  }
  const pages = Number(form.estimatedPages);
  if (!Number.isInteger(pages) || pages < 1) {
    errors.estimatedPages = 'Enter roughly how many pages, as a whole number.';
  } else if (pages > MAX_SELF_SERVE_PAGES) {
    errors.estimatedPages = `For more than ${MAX_SELF_SERVE_PAGES} pages, email us and we'll scope it together.`;
  }
  if (form.deadline && Number.isNaN(Date.parse(form.deadline))) {
    errors.deadline = 'Enter a valid date.';
  }
  if (files.length > MAX_FILES) {
    errors.files = `Attach up to ${MAX_FILES} files.`;
  } else {
    const problem = files.map(fileProblem).find(Boolean);
    if (problem) errors.files = problem;
  }
  return errors;
}
